import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  FONT_FAMILIES,
  FONT_SIZES,
  TEXT_ALIGNMENTS,
  BORDER_STYLES,
  ELEMENT_TYPES
} from '../../utils/constants';
import TableEditor from '../TableEditor';
import './style.css';

export default function StylePanel({ element, availableFields, onStyleChange }) {
  // Update style helper
  const updateStyle = (key, value) => {
    onStyleChange({
      style: {
        ...element?.style,
        [key]: value
      }
    });
  };

  // Update element property
  const updateProperty = (key, value) => {
    onStyleChange({ [key]: value });
  };

  if (!element) {
    return (
      <div className="style-panel">
        <div className="style-empty">
          <FontAwesomeIcon icon="mouse-pointer" />
          <p>Seleziona un elemento per modificare lo stile</p>
        </div>
      </div>
    );
  }

  const style = element.style || {};

  return (
    <div className="style-panel">
      {/* Element Content (for text elements) */}
      {element.type === ELEMENT_TYPES.TEXT && (
        <div className="style-group">
          <div className="style-group-title">Contenuto</div>
          <div className="style-row">
            <textarea
              className="style-input full-width"
              value={element.content || ''}
              onChange={e => updateProperty('content', e.target.value)}
              placeholder="Inserisci testo..."
              rows={3}
            />
          </div>
        </div>
      )}

      {/* Image upload (for image elements) */}
      {element.type === ELEMENT_TYPES.IMAGE && (
        <div className="style-group">
          <div className="style-group-title">Immagine</div>

          {element.src ? (
            <div className="image-preview-container">
              <img
                src={element.src}
                alt="Preview"
                className="image-preview"
              />
              <button
                className="image-remove-btn"
                onClick={() => updateProperty('src', null)}
                title="Rimuovi immagine"
              >
                <FontAwesomeIcon icon="times" />
              </button>
            </div>
          ) : (
            <div className="image-upload-placeholder">
              <FontAwesomeIcon icon="image" />
              <span>Nessuna immagine</span>
            </div>
          )}

          <div className="style-row" style={{ marginTop: 8 }}>
            <label className="image-upload-btn">
              <FontAwesomeIcon icon="upload" />
              <span>{element.src ? 'Cambia' : 'Carica'} immagine</span>
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    // Check file size (max 500KB for base64)
                    if (file.size > 500 * 1024) {
                      alert('Immagine troppo grande. Massimo 500KB.');
                      return;
                    }
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      updateProperty('src', event.target.result);
                    };
                    reader.readAsDataURL(file);
                  }
                  e.target.value = ''; // Reset input
                }}
              />
            </label>
          </div>

          <div className="style-row">
            <label className="style-label">Adatta</label>
            <select
              className="style-select"
              value={style.objectFit || 'contain'}
              onChange={e => updateStyle('objectFit', e.target.value)}
            >
              <option value="contain">Contenuto</option>
              <option value="cover">Riempi</option>
              <option value="fill">Adatta</option>
            </select>
          </div>
        </div>
      )}

      {/* Page Number Format */}
      {element.type === ELEMENT_TYPES.PAGE_NUMBER && (
        <div className="style-group">
          <div className="style-group-title">Formato</div>
          <div className="style-row">
            <input
              type="text"
              className="style-input full-width"
              value={element.format || 'Pagina {page} di {pages}'}
              onChange={e => updateProperty('format', e.target.value)}
              placeholder="Pagina {page} di {pages}"
            />
          </div>
          <div className="style-hint">
            Usa {'{page}'} e {'{pages}'} come segnaposto
          </div>
        </div>
      )}

      {/* Font Settings (hide for rectangles) */}
      {element.type !== ELEMENT_TYPES.RECTANGLE && (
        <div className="style-group">
          <div className="style-group-title">Carattere</div>

          <div className="style-row">
            <label className="style-label">Font</label>
            <select
              className="style-select"
              value={style.family || 'Arial'}
              onChange={e => updateStyle('family', e.target.value)}
            >
              {FONT_FAMILIES.map(font => (
                <option key={font} value={font}>{font}</option>
              ))}
            </select>
          </div>

          <div className="style-row">
            <label className="style-label">Dim.</label>
            <select
              className="style-select"
              value={style.size || 12}
              onChange={e => updateStyle('size', parseInt(e.target.value))}
            >
              {FONT_SIZES.map(size => (
                <option key={size} value={size}>{size}pt</option>
              ))}
            </select>
          </div>

          <div className="style-row">
            <label className="style-label">Stile</label>
            <div className="style-btn-group">
              <button
                className={`style-btn ${style.weight === 'bold' ? 'active' : ''}`}
                onClick={() => updateStyle('weight', style.weight === 'bold' ? 'normal' : 'bold')}
                title="Grassetto"
              >
                <FontAwesomeIcon icon="bold" />
              </button>
              <button
                className={`style-btn ${style.style === 'italic' ? 'active' : ''}`}
                onClick={() => updateStyle('style', style.style === 'italic' ? 'normal' : 'italic')}
                title="Corsivo"
              >
                <FontAwesomeIcon icon="italic" />
              </button>
            </div>
          </div>

          <div className="style-row">
            <label className="style-label">Colore</label>
            <input
              type="color"
              className="style-color"
              value={style.color || '#000000'}
              onChange={e => updateStyle('color', e.target.value)}
            />
            <input
              type="text"
              className="style-input"
              value={style.color || '#000000'}
              onChange={e => updateStyle('color', e.target.value)}
              style={{ flex: 1, marginLeft: 8 }}
            />
          </div>
        </div>
      )}

      {/* Alignment (hide for rectangles) */}
      {element.type !== ELEMENT_TYPES.RECTANGLE && (
        <div className="style-group">
          <div className="style-group-title">Allineamento</div>
          <div className="style-row">
            <label className="style-label">Testo</label>
            <div className="style-btn-group">
              {TEXT_ALIGNMENTS.map(align => (
                <button
                  key={align.value}
                  className={`style-btn ${style.textAlign === align.value ? 'active' : ''}`}
                  onClick={() => updateStyle('textAlign', align.value)}
                  title={align.label}
                >
                  <FontAwesomeIcon icon={align.icon} />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Background */}
      <div className="style-group">
        <div className="style-group-title">Sfondo</div>
        <div className="style-row">
          <label className="style-label">Colore</label>
          <input
            type="color"
            className="style-color"
            value={style.backgroundColor === 'transparent' ? '#ffffff' : (style.backgroundColor || '#ffffff')}
            onChange={e => updateStyle('backgroundColor', e.target.value)}
          />
          <button
            className={`style-btn ${style.backgroundColor === 'transparent' ? 'active' : ''}`}
            onClick={() => updateStyle('backgroundColor', 'transparent')}
            title="Trasparente"
            style={{ marginLeft: 8 }}
          >
            <FontAwesomeIcon icon="ban" />
          </button>
        </div>
      </div>

      {/* Border */}
      <div className="style-group">
        <div className="style-group-title">Bordo</div>

        <div className="style-row">
          <label className="style-label">Stile</label>
          <select
            className="style-select"
            value={style.borderStyle || 'none'}
            onChange={e => updateStyle('borderStyle', e.target.value)}
          >
            {BORDER_STYLES.map(bs => (
              <option key={bs.value} value={bs.value}>{bs.label}</option>
            ))}
          </select>
        </div>

        {style.borderStyle && style.borderStyle !== 'none' && (
          <>
            <div className="style-row">
              <label className="style-label">Spess.</label>
              <input
                type="number"
                className="style-input"
                value={style.borderWidth || 1}
                onChange={e => updateStyle('borderWidth', parseInt(e.target.value) || 0)}
                min="1"
                max="10"
              />
              <span className="style-unit">px</span>
            </div>

            <div className="style-row">
              <label className="style-label">Colore</label>
              <input
                type="color"
                className="style-color"
                value={style.borderColor || '#000000'}
                onChange={e => updateStyle('borderColor', e.target.value)}
              />
            </div>
          </>
        )}
      </div>

      {/* Dimensions */}
      <div className="style-group">
        <div className="style-group-title">Dimensioni (mm)</div>

        <div className="style-row">
          <label className="style-label">X</label>
          <input
            type="number"
            className="style-input"
            value={Math.round((element.x || 0) * 10) / 10}
            onChange={e => updateProperty('x', parseFloat(e.target.value) || 0)}
            step="0.5"
          />
        </div>

        <div className="style-row">
          <label className="style-label">Y</label>
          <input
            type="number"
            className="style-input"
            value={Math.round((element.y || 0) * 10) / 10}
            onChange={e => updateProperty('y', parseFloat(e.target.value) || 0)}
            step="0.5"
          />
        </div>

        <div className="style-row">
          <label className="style-label">Larghezza</label>
          <input
            type="number"
            className="style-input"
            value={Math.round((element.width || 50) * 10) / 10}
            onChange={e => updateProperty('width', parseFloat(e.target.value) || 10)}
            step="0.5"
            min="5"
          />
        </div>

        {/* Hide height for tables - they use auto height */}
        {element.type !== ELEMENT_TYPES.TABLE && (
          <div className="style-row">
            <label className="style-label">Altezza</label>
            <input
              type="number"
              className="style-input"
              value={Math.round((typeof element.height === 'number' ? element.height : 10) * 10) / 10}
              onChange={e => updateProperty('height', parseFloat(e.target.value) || 5)}
              step="0.5"
              min="3"
            />
          </div>
        )}
      </div>

      {/* Table-specific settings */}
      {element.type === ELEMENT_TYPES.TABLE && (
        <>
          {/* Table Column Editor */}
          <TableEditor
            element={element}
            availableFields={availableFields || []}
            onUpdateElement={onStyleChange}
          />

          {/* Table Style Settings */}
          <div className="style-group style-group-separated">
            <div className="style-group-title">Stile Tabella</div>

            <div className="style-row">
              <label className="style-label">Intestaz.</label>
              <label className="style-checkbox">
                <input
                  type="checkbox"
                  checked={style.showHeader !== false}
                  onChange={e => updateStyle('showHeader', e.target.checked)}
                />
                <span>Mostra</span>
              </label>
              <input
                type="color"
                className="style-color"
                value={style.headerBackground || '#f8f9fa'}
                onChange={e => updateStyle('headerBackground', e.target.value)}
                disabled={style.showHeader === false}
              />
            </div>

            <div className="style-row">
              <label className="style-label">Righe</label>
              <input
                type="color"
                className="style-color"
                value={style.rowBackground || '#ffffff'}
                onChange={e => updateStyle('rowBackground', e.target.value)}
              />
            </div>

            <div className="style-row">
              <label className="style-label">Riga Alt.</label>
              <input
                type="color"
                className="style-color"
                value={style.alternateRowBackground || '#f8f9fa'}
                onChange={e => updateStyle('alternateRowBackground', e.target.value)}
              />
            </div>
          </div>
        </>
      )}

      {/* Delete Button */}
      <div className="style-group">
        <button
          className="delete-element-btn"
          onClick={() => onStyleChange({ _delete: true })}
        >
          <FontAwesomeIcon icon="trash" />
          Elimina Elemento
        </button>
      </div>
    </div>
  );
}
